import { useState } from 'react'

import { ensureEpisodePages } from '../../lib/comicPages'
import type { ComicEpisode, ComicEpisodeEditPatch } from '../../lib/comicTypes'
import { formatShelfDate } from '../../lib/dateDisplay'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { chromeToggleChipClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { ManagementListBody } from '../ManagementListModal'
import { ThemeModal } from '../modal'
import { ComicEpisodeEditModal } from './ComicEpisodeEditModal'
import { ComicRailEditButton } from './ComicRailSection'
import { type ComicSaveStatus, saveStatusLabel } from './ComicSaveStatusPill'

type Props = {
  open: boolean
  angular: boolean
  episodes: ComicEpisode[]
  activeEpisodeId: string | null
  canvasEpisodeIds: string[]
  episodeSaveStatus: (episodeId: string) => ComicSaveStatus
  onClose: () => void
  onToggleCanvasEpisode: (episodeId: string, checked: boolean) => void
  onEditEpisode?: (episodeId: string, patch: ComicEpisodeEditPatch) => void
  onExportCanvasEpisodes?: () => void
  exportingCanvasZip?: boolean
}

export function ComicEpisodeDirectoryModal({
  open,
  angular,
  episodes,
  activeEpisodeId,
  canvasEpisodeIds,
  episodeSaveStatus,
  onClose,
  onToggleCanvasEpisode,
  onEditEpisode,
  onExportCanvasEpisodes,
  exportingCanvasZip = false,
}: Props) {
  const [editOpen, setEditOpen] = useState(false)
  const [editTargetId, setEditTargetId] = useState<string | null>(null)
  const editTarget = episodes.find((e) => e.id === editTargetId) ?? null

  const entries = episodes.map((ep) => {
    const pageCount = ensureEpisodePages(ep).pages.length
    const onCanvas = canvasEpisodeIds.includes(ep.id)
    const status = episodeSaveStatus(ep.id)
    const createdLabel = ep.createdAt ? formatShelfDate(ep.createdAt) : '—'
    const isActive = ep.id === activeEpisodeId

    return {
      id: ep.id,
      primary: ep.title,
      badge: isActive ? '编辑中' : undefined,
      detail: `${pageCount} 页 · 创建于 ${createdLabel}`,
      statusIndicator: (
        <ChromeTooltip content={saveStatusLabel(status)} angular={angular} side="top" align="center">
          <span
            className={[
              'inline-block h-1.5 w-1.5 rounded-full',
              status === 'saving'
                ? 'bg-[rgb(234_179_89)]'
                : status === 'dirty'
                  ? 'bg-[rgb(236_148_157)]'
                  : onCanvas
                    ? 'bg-[var(--accent)]'
                    : 'bg-gray-400',
            ].join(' ')}
            aria-label={saveStatusLabel(status)}
          />
        </ChromeTooltip>
      ),
      actions: (
        <>
          <button
            type="button"
            className={chromeToggleChipClass(angular, onCanvas)}
            onClick={() => onToggleCanvasEpisode(ep.id, !onCanvas)}
            aria-pressed={onCanvas}
          >
            {onCanvas ? '已加入画布' : '加入画布'}
          </button>
          {onEditEpisode ? (
            <ComicRailEditButton
              angular={angular}
              label={`编辑${ep.title}`}
              tooltip="编辑"
              onClick={() => {
                setEditTargetId(ep.id)
                setEditOpen(true)
              }}
            />
          ) : null}
        </>
      ),
    }
  })

  return (
    <>
      <ThemeModal
        open={open}
        onClose={onClose}
        title="话次目录"
        angular={angular}
        size="md"
        bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            {onExportCanvasEpisodes ? (
              <ChromeToolbarButton
                type="button"
                disabled={canvasEpisodeIds.length === 0 || exportingCanvasZip}
                onClick={onExportCanvasEpisodes}
              >
                {exportingCanvasZip ? '打包中…' : `导出已选话次 ZIP（${canvasEpisodeIds.length}）`}
              </ChromeToolbarButton>
            ) : (
              <span />
            )}
            <ChromePrimaryButton density="compact" type="button" onClick={onClose}>
              完成
            </ChromePrimaryButton>
          </div>
        }
      >
        <ManagementListBody entries={entries} angular={angular} emptyText="暂无话次" />
      </ThemeModal>

      {onEditEpisode ? (
        <ComicEpisodeEditModal
          open={editOpen}
          angular={angular}
          episode={editTarget}
          onClose={() => setEditOpen(false)}
          onSave={(patch) => {
            if (editTargetId) onEditEpisode(editTargetId, patch)
          }}
        />
      ) : null}
    </>
  )
}
